import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { apiGetSystemConfigValue } from "api/api";
import {
  PUBLIC_CONFIG_GROUP,
  buildRemoteKey,
  configSchema,
  getDefaults,
  isLocalizedField,
} from "config/siteContent";

// # 同一次瀏覽內共用，避免每頁重抓（依語言分開存）
const cache = new Map();

const readValue = (result) => {
  if (result.status !== "fulfilled") return null; // 404 代表尚未設定
  const value = result.value?.data?.content?.value;
  return typeof value === "string" && value !== "" ? value : null;
};

/**
 * 讀取某個頁面（section）由後台維護的文案與開關。
 *
 * 後台每個文案欄位有中英兩筆（英文 key 結尾加 En）。切到英文時優先取英文那筆，
 * 後台沒填英文就退回中文那筆，中文也沒設定才用內建預設值，
 * 所以畫面不會因為後台漏填而空白。開關類欄位不分語言。
 */
export const useSiteConfig = (section) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "zhTW";
  const cacheKey = `${section}:${lang}`;

  const defaults = getDefaults(section);
  const [values, setValues] = useState(() => cache.get(cacheKey) || defaults);

  useEffect(() => {
    let cancelled = false;

    if (cache.has(cacheKey)) {
      setValues(cache.get(cacheKey));
      return undefined;
    }

    const fields = configSchema[section] || [];

    // 英文時同時取英文與中文兩筆，英文沒填就用中文，避免整段變成內建預設
    const requests = fields.map((field) => {
      const keys = [buildRemoteKey(section, field.key)];
      if (lang === "en" && isLocalizedField(field)) {
        keys.unshift(buildRemoteKey(section, field.key, "en"));
      }
      return keys;
    });

    const fetchConfig = async () => {
      const results = await Promise.allSettled(
        requests
          .flat()
          .map((key) =>
            apiGetSystemConfigValue({ group: PUBLIC_CONFIG_GROUP, key }),
          ),
      );

      const merged = { ...defaults };
      let cursor = 0;
      requests.forEach((keys, index) => {
        const slice = results.slice(cursor, cursor + keys.length);
        cursor += keys.length;
        const hit = slice.map(readValue).find((v) => v !== null);
        if (hit !== undefined) merged[fields[index].key] = hit;
      });

      cache.set(cacheKey, merged);
      if (!cancelled) setValues(merged);
    };

    fetchConfig();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [cacheKey]);

  const get = (key) => values[key] ?? defaults[key] ?? "";

  // # 取開關（SystemConfig 的 value 是字串）
  const isOn = (key) => {
    const raw = values[key] ?? defaults[key];
    if (typeof raw === "boolean") return raw;
    return String(raw).toLowerCase() === "true";
  };

  // # 取多行文字，拆成陣列
  const getLines = (key) =>
    String(get(key) || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

  return { values, get, isOn, getLines };
};

export default useSiteConfig;
